import type { ReactNode } from 'react';

/**
 * A sortable, server-paginated table.
 *
 * Everything visual belongs here; fetching, URL sync and the shape of a record belong to
 * the route that uses it. The rows handed in are one page, already filtered and ordered by
 * the server — this component never sorts or slices.
 *
 * Density is Comfortable by default because that is what the main app will want. The admin
 * console opts into Compact and shows no toggle.
 */

export type DataTableDensity = 'comfortable' | 'compact';

export interface DataTableColumn<T> {
  key: string;
  label: string;
  align?: 'left' | 'right';
  sortable?: boolean;
  /**
   * Lets a cell wrap onto more lines instead of staying on one.
   *
   * Cells are nowrap by default, which keeps columns of dates and numbers tidy. Set this on
   * any column whose content has no natural width limit, or it will widen the page.
   */
  wrap?: boolean;
  render?: (row: T) => ReactNode;
}

export interface DataTableRowAction {
  label: string;
  onSelect: () => void;
  tone?: 'default' | 'danger';
}

export interface DataTableProps<T> {
  /** Names the table for screen readers; rendered as a visually hidden caption. */
  caption: string;
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  total: number;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
  loading: boolean;
  onSortChange: (key: string) => void;
  onPageChange: (page: number) => void;
  emptyLabel?: ReactNode;
  density?: DataTableDensity;
  zebra?: boolean;
  rowClassName?: (row: T) => string | undefined;
  /**
   * Makes the whole row a click target for its detail view.
   *
   * This is a convenience over the real affordance, never the only one: a row that opens
   * something must still contain a genuine link or button, because a `<tr>` cannot be
   * focused, tabbed to or announced as actionable. A click that lands inside any
   * interactive element is left to that element rather than handled twice.
   */
  onRowClick?: (row: T) => void;
  /** Reserved for the row-actions menu in the design. Nothing renders one yet. */
  rowActions?: (row: T) => DataTableRowAction[];
}

const SKELETON_ROWS = 8;

function cellClass<T>(column: DataTableColumn<T>): string | undefined {
  const names = [
    column.align === 'right' ? 'data-table-right' : '',
    column.wrap === true ? 'data-table-cell-wrap' : '',
  ].filter(Boolean);
  return names.length === 0 ? undefined : names.join(' ');
}

/** "1–30 of 214", or "No results". Exported for the heading block above the table. */
export function formatRange(page: number, pageSize: number, total: number): string {
  if (total === 0) return 'No results';
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);
  return `${first}–${last} of ${total}`;
}

export function totalPagesFor(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * At most five numbered pages, centred on the current one and clamped to both ends, so the
 * pager never changes width as an agent walks through the pages.
 */
export function pageWindow(page: number, totalPages: number): number[] {
  const from = Math.max(1, Math.min(page - 2, totalPages - 4));
  const to = Math.min(totalPages, from + 4);
  const pages: number[] = [];
  for (let i = from; i <= to; i += 1) pages.push(i);
  return pages;
}

export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  total,
  page,
  pageSize,
  sort,
  dir,
  loading,
  onSortChange,
  onPageChange,
  emptyLabel,
  density = 'comfortable',
  zebra = true,
  rowClassName,
  onRowClick,
}: DataTableProps<T>) {
  const totalPages = totalPagesFor(total, pageSize);
  const empty = !loading && rows.length === 0;

  return (
    <div className={`data-table-wrap data-table-wrap--${density}`}>
      <p className="sr-only" role="status" aria-live="polite">
        {loading ? 'Loading results' : formatRange(page, pageSize, total)}
      </p>

      <div className="data-table-scroll">
        <table className="data-table">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => {
                const active = column.key === sort;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={column.align === 'right' ? 'data-table-right' : undefined}
                    aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    {column.sortable === false ? (
                      <span className="data-table-label">{column.label}</span>
                    ) : (
                      <button
                        type="button"
                        className="data-table-sort"
                        title="Sort by this column"
                        onClick={() => onSortChange(column.key)}
                      >
                        {column.label}
                        {/* aria-sort already announces the direction, so this is decorative. */}
                        <span
                          className={`data-table-glyph ${active ? '' : 'is-idle'}`}
                          aria-hidden="true"
                        >
                          {active && dir === 'asc' ? '↑' : '↓'}
                        </span>
                      </button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading
              ? // Only the body swaps, so the heading, toolbar, header row and pager all stay
                // put and the page does not jump between fetches.
                Array.from({ length: SKELETON_ROWS }, (_unused, index) => (
                  <tr key={`skeleton-${index}`}>
                    {columns.map((column) => (
                      <td key={column.key}>
                        <span className="data-table-skeleton" />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row, index) => (
                  <tr
                    key={rowKey(row)}
                    className={[
                      zebra && index % 2 === 1 ? 'data-table-row--alt' : '',
                      onRowClick != null ? 'data-table-row--clickable' : '',
                      rowClassName?.(row) ?? '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={
                      onRowClick == null
                        ? undefined
                        : (event) => {
                            // The row is the large target; the link or button inside it is
                            // the real control. Letting both fire would navigate twice, and
                            // would hijack a click meant for a row action.
                            if (
                              event.target instanceof Element &&
                              event.target.closest('a, button, input, select, textarea, label') !=
                                null
                            ) {
                              return;
                            }
                            // A drag to select an email out of the row is not a click on it.
                            if ((window.getSelection()?.toString() ?? '') !== '') return;
                            onRowClick(row);
                          }
                    }
                  >
                    {columns.map((column) => (
                      <td key={column.key} className={cellClass(column)}>
                        {column.render?.(row)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {empty && (
        <div className="data-table-empty">
          <strong>No matches</strong>
          {emptyLabel != null && <span className="hint">{emptyLabel}</span>}
        </div>
      )}

      {!empty && totalPages > 1 && (
        <nav className="data-table-pager" aria-label="Pagination">
          <PagerStep
            label="First page"
            glyph="«"
            to={1}
            disabled={page === 1}
            onGo={onPageChange}
          />
          <PagerStep
            label="Previous page"
            glyph="‹"
            to={page - 1}
            disabled={page === 1}
            onGo={onPageChange}
          />
          {pageWindow(page, totalPages).map((number) => (
            <button
              key={number}
              type="button"
              className={`result-pager-arrow data-table-page ${number === page ? 'is-active' : ''}`}
              aria-current={number === page ? 'page' : undefined}
              onClick={() => onPageChange(number)}
            >
              {number}
            </button>
          ))}
          <PagerStep
            label="Next page"
            glyph="›"
            to={page + 1}
            disabled={page === totalPages}
            onGo={onPageChange}
          />
          <PagerStep
            label="Last page"
            glyph="»"
            to={totalPages}
            disabled={page === totalPages}
            onGo={onPageChange}
          />
        </nav>
      )}
    </div>
  );
}

function PagerStep({
  label,
  glyph,
  to,
  disabled,
  onGo,
}: {
  label: string;
  glyph: string;
  to: number;
  disabled: boolean;
  onGo: (page: number) => void;
}) {
  return (
    <button
      type="button"
      className="result-pager-arrow data-table-page"
      aria-label={label}
      disabled={disabled}
      onClick={() => onGo(to)}
    >
      {glyph}
    </button>
  );
}
